/**
 * `TransferCreditsModal` — move N session credits from THIS member (A) to
 * another member (B). Dispatches the `transfer_member_credits` canonical
 * action, which in one atomic tx decrements A's chosen source and mints a
 * `source='transfer'` grant on B.
 *
 * The source is one of A's packages OR, since 2026-10-29, one of A's
 * memberships. A package transfer inherits A's session type + expiry. A
 * membership has neither to inherit, so the modal names the destination
 * carrier for the membership's session type (`CUSTOM_PKG_CARRIER_SLUG`, the
 * custom-pack model) and the action mints a never-expiring row — membership
 * credits roll over, so a transfer must not invent an expiry.
 *
 * A's packages + memberships are threaded down from the already-loaded member
 * detail (no extra fetch; eligibility mirrors `SwapCreditModal`); the
 * destination member is found with the same debounced
 * `lookupOptions("members", q)` search the calendar uses.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import {
  api,
  type MemberDetailMembership,
  type MemberDetailPackage,
} from "../lib/api";
import { dispatchAction } from "../lib/dispatch";
import { CUSTOM_PKG_CARRIER_SLUG } from "./PurchaseCreditsModal";

interface MemberOption {
  readonly value: string;
  readonly label: string;
  readonly sublabel?: string;
}

/** One of A's credit rows the operator can draw from. */
interface TransferSource {
  readonly kind: "package" | "membership";
  readonly id: string;
  readonly label: string;
  readonly remaining: number;
  /**
   * Membership only — the catalog carrier B's row is minted on, picked by the
   * plan's session type (home → home carrier, everything else → studio, the
   * same default the Sessions card uses). `null` for a package source.
   */
  readonly toPackageSlug: string | null;
}

export function TransferCreditsModal({
  fromMemberId,
  fromMemberName,
  sourcePackages,
  sourceMemberships,
  onDone,
}: {
  readonly fromMemberId: string;
  readonly fromMemberName?: string;
  /** A's packages (from the loaded member detail); filtered to transferable. */
  readonly sourcePackages: ReadonlyArray<MemberDetailPackage>;
  /** A's memberships (same source); filtered to live, finite, positive. */
  readonly sourceMemberships: ReadonlyArray<MemberDetailMembership>;
  /** Called after a successful transfer (refresh the member page). */
  readonly onDone: () => void;
}): JSX.Element {
  // Eligible sources: an active package with credits left, or a live
  // (lifecycle + status active, not paused) membership with a FINITE positive
  // balance — an unlimited plan holds no count to move.
  const sources = useMemo<ReadonlyArray<TransferSource>>(() => {
    const out: TransferSource[] = [];
    for (const p of sourcePackages) {
      if (p.status !== "active" || p.remaining_sessions <= 0) continue;
      out.push({
        kind: "package",
        id: p.id,
        label: p.label,
        remaining: p.remaining_sessions,
        toPackageSlug: null,
      });
    }
    for (const m of sourceMemberships) {
      if (m.lifecycle_state !== "active" || m.status !== "active") continue;
      if (m.paused) continue;
      if (m.remaining_sessions === null || m.remaining_sessions <= 0) continue;
      out.push({
        kind: "membership",
        id: m.id,
        label: `${m.membership_slug} (membership)`,
        remaining: m.remaining_sessions,
        toPackageSlug:
          m.session_type === "home"
            ? CUSTOM_PKG_CARRIER_SLUG.home
            : CUSTOM_PKG_CARRIER_SLUG.studio,
      });
    }
    return out;
  }, [sourcePackages, sourceMemberships]);

  const [choice, setChoice] = useState(
    sources[0] ? `${sources[0].kind}:${sources[0].id}` : "",
  );
  const selectedSource = sources.find((s) => `${s.kind}:${s.id}` === choice);
  const maxSessions = Math.min(selectedSource?.remaining ?? 0, 200);

  const [sessions, setSessions] = useState("1");
  const [destQuery, setDestQuery] = useState("");
  const [destOptions, setDestOptions] = useState<
    ReadonlyArray<MemberOption> | null
  >(null);
  const [searching, setSearching] = useState(false);
  const [destId, setDestId] = useState("");
  const [destLabel, setDestLabel] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);

  // Debounced destination-member search (name / email / phone ILIKE), same as
  // the calendar's book-member picker. Hidden until the operator types.
  useEffect(() => {
    const q = destQuery.trim();
    if (q === "" || destId !== "") {
      setDestOptions(null);
      setSearching(false);
      return;
    }
    let cancelled = false;
    setSearching(true);
    const handle = setTimeout(() => {
      void (async () => {
        try {
          const res = await api.lookupOptions("members", q);
          if (!cancelled) {
            // Never offer the source member as a destination.
            setDestOptions(res.options.filter((o) => o.value !== fromMemberId));
          }
        } catch (e) {
          if (!cancelled) setError(`Couldn't load members: ${(e as Error).message}`);
        } finally {
          if (!cancelled) setSearching(false);
        }
      })();
    }, 220);
    return () => {
      cancelled = true;
      clearTimeout(handle);
    };
  }, [destQuery, destId, fromMemberId]);

  const submit = useCallback(async () => {
    const n = Number(sessions.trim());
    if (!Number.isInteger(n) || n < 1 || n > maxSessions) {
      setError(`Enter a whole number of sessions (1–${maxSessions}).`);
      return;
    }
    if (selectedSource === undefined) {
      setError("Pick a source.");
      return;
    }
    if (destId === "") {
      setError("Pick a destination member.");
      return;
    }
    if (destId === fromMemberId) {
      setError("Can't transfer credits to the same member.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const r = await dispatchAction("transfer_member_credits", {
        from_member_id: fromMemberId,
        to_member_id: destId,
        sessions: n,
        ...(selectedSource.kind === "package"
          ? { source_package_id: selectedSource.id }
          : {
              source_membership_id: selectedSource.id,
              to_package_slug: selectedSource.toPackageSlug,
            }),
        ...(note.trim() !== "" ? { note: note.trim() } : {}),
      });
      setBusy(false);
      if (!r.ok) {
        setError(`Transfer failed: ${r.message || r.code}`);
        return;
      }
      setDone(
        `Transferred ${n} session${n === 1 ? "" : "s"} to ${destLabel || "the member"} from ${selectedSource.label}.`,
      );
      onDone();
    } catch (e) {
      setBusy(false);
      setError((e as Error).message);
    }
  }, [
    sessions,
    maxSessions,
    selectedSource,
    destId,
    destLabel,
    fromMemberId,
    note,
    onDone,
  ]);

  // The confirmation wins over the empty state: a transfer that drains A's
  // last source re-renders with no sources, and must still read as a success
  // (it read "has no active credits to transfer" before 2026-10-29).
  if (done !== null) {
    return <p className="xfer-done">✅ {done}</p>;
  }

  if (sources.length === 0) {
    return (
      <p className="xfer-empty">
        {fromMemberName ?? "This member"} has no active credits to transfer.
      </p>
    );
  }

  return (
    <div className="xfer-modal">
      <label className="xfer-field">
        <span>Source</span>
        <select
          value={choice}
          onChange={(e) => {
            setChoice(e.target.value);
            setSessions("1");
          }}
        >
          {sources.map((s) => (
            <option key={`${s.kind}:${s.id}`} value={`${s.kind}:${s.id}`}>
              {s.label} · {s.remaining} left
            </option>
          ))}
        </select>
      </label>
      {selectedSource?.kind === "membership" ? (
        <p className="xfer-hint">
          Membership credits roll over, so the moved credits never expire on
          the recipient. They land as{" "}
          {selectedSource.toPackageSlug === CUSTOM_PKG_CARRIER_SLUG.home
            ? "home"
            : "studio"}{" "}
          session credits.
        </p>
      ) : null}

      <label className="xfer-field">
        <span>Sessions to move (max {maxSessions})</span>
        <input
          type="number"
          min={1}
          max={maxSessions}
          value={sessions}
          onChange={(e) => setSessions(e.target.value)}
        />
      </label>

      <div className="xfer-field">
        <span>Destination member</span>
        {destId !== "" ? (
          <div className="xfer-dest-chip">
            <span>{destLabel}</span>
            <button
              type="button"
              className="xfer-dest-clear"
              onClick={() => {
                setDestId("");
                setDestLabel("");
                setDestQuery("");
              }}
            >
              Change
            </button>
          </div>
        ) : (
          <>
            <input
              type="text"
              placeholder="Search by name, email or phone…"
              value={destQuery}
              onChange={(e) => setDestQuery(e.target.value)}
            />
            {searching ? <p className="xfer-hint">Searching…</p> : null}
            {destOptions !== null && destOptions.length > 0 ? (
              <ul className="xfer-dest-list">
                {destOptions.map((o) => (
                  <li key={o.value}>
                    <button
                      type="button"
                      onClick={() => {
                        setDestId(o.value);
                        setDestLabel(o.label);
                        setDestOptions(null);
                      }}
                    >
                      {o.label}
                      {o.sublabel ? (
                        <span className="xfer-dest-sub"> · {o.sublabel}</span>
                      ) : null}
                    </button>
                  </li>
                ))}
              </ul>
            ) : null}
            {destOptions !== null &&
            destOptions.length === 0 &&
            !searching ? (
              <p className="xfer-hint">No members match.</p>
            ) : null}
          </>
        )}
      </div>

      <label className="xfer-field">
        <span>Note (optional)</span>
        <input
          type="text"
          maxLength={500}
          placeholder="e.g. moved from partner's account"
          value={note}
          onChange={(e) => setNote(e.target.value)}
        />
      </label>

      <div className="xfer-actions">
        <button
          type="button"
          className="xfer-submit"
          disabled={busy || destId === "" || maxSessions < 1}
          onClick={() => void submit()}
        >
          {busy ? "Transferring…" : "Transfer credits"}
        </button>
      </div>
      {error !== null ? <p className="cal-action-error">{error}</p> : null}
    </div>
  );
}
